import { css } from '@emotion/css';
import { Alert, theme } from 'antd';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { PDFJS_SCOPE_CLASS } from '../../vite/pdfjsAssetBase';
import { LoadingIndicator } from '../LoadingIndicator';
import { PdfToolbar, type PdfToolbarVisibility } from './PdfToolbar';
import type { PdfSource } from './pdfSource';
import { usePdfDocument } from './usePdfDocument';
import { RELATIVE_ZOOM_MODES } from './zoomLevels';

export type ZoomValue = 'auto' | 'page-width' | 'page-fit' | 'page-actual' | number;

export interface PdfViewerProps {
  src: PdfSource;
  /** Changing this reloads the document, so keep the object referentially stable. */
  httpHeaders?: Record<string, string>;
  withCredentials?: boolean;
  initialZoom?: ZoomValue;
  /** Toolbar groups to show. Everything is on unless switched off here. */
  toolbar?: PdfToolbarVisibility;
  onError?: (error: Error) => void;
}

const ALL_VISIBLE: PdfToolbarVisibility = {};

export const PdfViewer: React.FC<PdfViewerProps> = ({
  src,
  httpHeaders,
  withCredentials,
  initialZoom = 'auto',
  toolbar = ALL_VISIBLE,
  onError,
}) => {
  const { token } = theme.useToken();
  const containerRef = useRef<HTMLDivElement>(null);
  const { viewer, eventBus, findController, status, error } = usePdfDocument({
    containerRef,
    src,
    httpHeaders,
    withCredentials,
    initialZoom,
    onError,
  });

  // Relative zoom modes do not recompute themselves when the container resizes;
  // re-assigning the same value forces a recalculation.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !viewer || status !== 'ready') {
      return;
    }
    const observer = new ResizeObserver(entries => {
      // A hidden container (a closed modal, say) reports 0×0. Rescaling then makes pdf.js
      // try to scroll a display:none element, which it cannot do — it logs "offsetParent
      // is not set". There is nothing worth recomputing while invisible anyway.
      const rect = entries[0]?.contentRect;
      if (!rect || rect.width === 0 || rect.height === 0) {
        return;
      }
      const current = viewer.currentScaleValue;
      if (RELATIVE_ZOOM_MODES.includes(current)) {
        viewer.currentScaleValue = current;
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [viewer, status]);

  const rootStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${token.colorBgLayout};
    /* antd's tokens only reach its own components; plain text would otherwise
       inherit the ambient body font and size and not match the controls beside it. */
    font-family: ${token.fontFamily};
    font-size: ${token.fontSize}px;
  `;

  const viewportStyle = css`
    position: relative;
    flex: 1;
    min-height: 0;
  `;

  const containerStyle = css`
    position: absolute;
    inset: 0;
    overflow: auto;
  `;

  // Positioned so it paints above the absolutely positioned scroll container,
  // which would otherwise cover a static sibling.
  const overlayStyle = css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${token.padding}px;
  `;

  return (
    // PDFJS_SCOPE_CLASS carries pdf.js's own stylesheet, which the Vite plugin
    // rewrites from :root onto this element.
    <div className={`${PDFJS_SCOPE_CLASS} ${rootStyle}`}>
      <PdfToolbar viewer={viewer} eventBus={eventBus} findController={findController} visibility={toolbar} />
      <div className={viewportStyle}>
        <div ref={containerRef} className={containerStyle}>
          <div className="pdfViewer" />
        </div>
        {status === 'loading' && (
          <div className={overlayStyle}>
            <LoadingIndicator />
          </div>
        )}
        {status === 'error' && (
          <div className={overlayStyle}>
            <Alert type="warning" description={`Das PDF konnte nicht geladen werden: ${error?.message ?? ''}`} />
          </div>
        )}
      </div>
    </div>
  );
};
